import {configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {combineReducers} from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {Configuration} from 'configuration';
import advancedSearchItemsSlice from '../services/advancedSearchItems/reducer';
import advancedSearchViewModeSlice from '../services/advancedSearchViewMode/reducer';
import agentItemsSlice from '../services/agentItems/reducer';
import bulkUpdateItemsSlice from '../services/bulkUpdateItems/reducer';
import codelistsSlice from '../services/codelists/codelistsSlice';
import {elasticApi} from '../services/elastic';
import headSlice from '../services/head/headSlice';
import mergeSuggestionQueueSlice from '../services/mergeSuggestions/mergeSuggestionQueueSlice';
import metadataEditSlice from '../services/metadata/metadataEditSlice';
import receivedItemsSlice from '../services/receiveItems/receivedItemsSlice';
import settingsSlice from '../services/settings/settingsSlice';
import thesaurusSlice from '../services/thesaurus/thesaurusSlice';
import workItemsSlice from '../services/workItems/workItemsSlice';
import {ebsApi} from './ebsApi';
import {localApi} from './localApi';

const reducers = {
  [ebsApi.reducerPath]: ebsApi.reducer,
  [localApi.reducerPath]: localApi.reducer,
  [elasticApi.reducerPath]: elasticApi.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [headSlice.name]: headSlice.reducer,
  [codelistsSlice.name]: codelistsSlice.reducer,
  [thesaurusSlice.name]: thesaurusSlice.reducer,
  [metadataEditSlice.name]: metadataEditSlice.reducer,
  [receivedItemsSlice.name]: receivedItemsSlice.reducer,
  [bulkUpdateItemsSlice.name]: bulkUpdateItemsSlice.reducer,
  [advancedSearchItemsSlice.name]: advancedSearchItemsSlice.reducer,
  [workItemsSlice.name]: workItemsSlice.reducer,
  [agentItemsSlice.name]: agentItemsSlice.reducer,
  [advancedSearchViewModeSlice.name]: advancedSearchViewModeSlice.reducer,
  [mergeSuggestionQueueSlice.name]: mergeSuggestionQueueSlice.reducer,
};
const rootReducer = combineReducers(reducers);

const whitelist: string[] = [
  settingsSlice.name,
  bulkUpdateItemsSlice.name,
  workItemsSlice.name,
  agentItemsSlice.name,
  advancedSearchViewModeSlice.name,
  mergeSuggestionQueueSlice.name,
];

const migrations = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  1: (state: any) => {
    // Migration from version 0 to 1
    return {
      ...state,
      settings: {
        ...state.settings,
        mergeSuggestionList: {
          currentType: 'SERIES',
          showUserCreated: false,
        },
      },
    };
  },
};

const persistConfig = {
  key: 'ebba',
  storage,
  whitelist,
  version: 1,
  migrate: createMigrate(migrations, {debug: Configuration.env === 'local'}),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: Configuration.env === 'local',
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: {warnAfter: 101},
      serializableCheck: {
        warnAfter: 100,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(ebsApi.middleware)
      .concat(localApi.middleware)
      .concat(elasticApi.middleware),
});

setupListeners(store.dispatch);

const persistor = persistStore(store);

export default {store, persistor};
